import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { Button, ButtonGroup } from 'reactstrap';

import { OnFilterChange } from '../../../utils/models/Objective';
import { getContrastColor } from '../../../utils/util/color';
import { ObjectiveType } from '../ObjectiveTypeOperations';

const SCOPE_ORDER: Record<string, number> = {
  organization: 0,
  individual: 1,
};

type Props = {
  disabled: boolean;
  value: number[];
  objectiveTypes: ObjectiveType[];
  onChange: (delta: OnFilterChange) => void;
};

const ObjectivesOverviewTypeFilter: FC<Props> = ({
  value,
  objectiveTypes,
  onChange,
  disabled,
}) => {
  const handleOnChange = useCallback(
    (typeId: number) => {
      if (value.includes(typeId)) {
        onChange({ objectiveTypeIds: value.filter((id) => id !== typeId) });
      } else {
        onChange({ objectiveTypeIds: [...value, typeId] });
      }
    },
    [value, onChange]
  );

  // Sort: organization types first, individual last
  const sortedTypes = useMemo(
    () =>
      [...objectiveTypes].sort(
        (a, b) => (SCOPE_ORDER[a.scope] ?? 99) - (SCOPE_ORDER[b.scope] ?? 99)
      ),
    [objectiveTypes]
  );

  // Hide if only one type (individual) — nothing useful to filter
  if (sortedTypes.length <= 1) {
    return null;
  }

  return (
    <ButtonGroup className="w-100">
      {sortedTypes.map((ot) => {
        const isActive = value.includes(ot.id);
        const hasColor = !!ot.color;
        return (
          <Button
            key={ot.id}
            disabled={disabled}
            outline={!isActive && !hasColor}
            color={hasColor ? undefined : 'secondary'}
            className={hasColor ? 'btn' : undefined}
            onClick={() => handleOnChange(ot.id)}
            style={
              hasColor
                ? {
                    backgroundColor: ot.color,
                    borderColor: ot.color,
                    color: getContrastColor(ot.color),
                    opacity: isActive ? 1 : 0.4,
                  }
                : undefined
            }
          >
            {ot.name}
          </Button>
        );
      })}
    </ButtonGroup>
  );
};

export default ObjectivesOverviewTypeFilter;
